import { useState, useEffect, useRef, useCallback, type MouseEvent } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams, Outlet } from 'react-router-dom';
import { Home } from './components/Home';
import { Explore } from './components/Explore';
import { Library } from './components/Library';
import { Navigation } from './components/Navigation';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { AuthModal } from './components/AuthModal';
import { UserProfile } from './components/UserProfile';
import { MySeries } from './components/MySeries';
import { CreatorLanding } from './components/CreatorLanding';
import { SeriesLanding } from './components/SeriesLanding';
import { AdminPanel } from './components/AdminPanel';
import { routeBuilders, normalizePath } from './routes';
import { slugify } from '../utils/slug';
// Redeploy trigger: refreshing bundles to clear stale cached player assets.
import { DockPlayer } from './player/DockPlayer';
import { QueueDrawer } from './player/QueueDrawer';
import { FullPlayer } from './player/FullPlayer';
import { usePlayerController } from './player/PlayerProvider';
import { usePlayerStore } from './player/playerStore';
import {
  extractImageStoragePath,
  getDisplayImageUrl as fetchDisplayImageUrl,
  MEDIA_CONSTANTS,
} from './utils/media';

const FALLBACK_COVER = 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=800&q=80';


const DEMO_USER_ID = import.meta.env.VITE_SUPABASE_DEMO_USER_ID as string | undefined;

type SeriesQueryResult = {
  id: string;
  title: string;
  description: string | null;
  cover_image_url: string | null;
  release_schedule: string | null;
  slug: string | null;
  status?: string | null;
  creator: {
    id: string;
    display_name: string | null;
  } | null;
  series_genres?: Array<{
    genre_id: string;
    genres?: {
      id: string;
      name: string;
      slug: string | null;
    } | null;
  }>;
  episodes?: Array<{
    duration_seconds: number | null;
    slug?: string | null;
  }>;
};

type Story = {
  id: string;
  title: string;
  slug?: string | null;
  author: string;
  creatorId?: string | null;
  creatorSlug?: string | null;
  creatorName?: string | null;
  coverImage: string;
  duration: string;
  totalChapters: number;
  genreIds: string[];
  description?: string | null;
  releaseSchedule?: string | null;
  listens?: number;
};

type ContinueListeningEntry = {
  seriesId: string;
  seriesSlug?: string | null;
  story: Story | null;
  coverImage: string;
  creatorName?: string | null;
  creatorSlug?: string | null;
  episodeId: string;
  episodeTitle: string;
  episodeSlug?: string | null;
  episodeNumber?: number | null;
  positionSeconds: number;
  durationSeconds?: number | null;
  lastListenedAt?: string | null;
};

type CreatorSpotlight = {
  id: string;
  displayName: string;
  avatarUrl: string;
  followerCount: number;
  seriesCount: number;
  slug: string;
};

type GenreRecord = { id: string; name: string; slug: string };

type UserProfileData = {
  display_name: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  role?: string | null;
};

const formatTotalDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) {
    return 'Listen now';
  }
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  }
  const secs = Math.floor(seconds % 60);
  return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
};

const formatEpisodeDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) {
    return '0:00';
  }
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const normalizeSeriesToStory = (series: SeriesQueryResult, slugOverride?: string): Story => {
  const totalDurationSeconds = (series.episodes ?? []).reduce((sum, episode) => sum + (episode.duration_seconds ?? 0), 0);
  const genreIds = (series.series_genres ?? [])
    .map((entry) => entry.genres?.id ?? entry.genre_id)
    .filter((id): id is string => Boolean(id));
  const creatorName = series.creator?.display_name ?? 'Unknown Creator';
  const creatorSlug = slugify(creatorName || series.creator?.id || 'creator');
  const seriesSlug = slugOverride ?? series.slug ?? slugify(series.title || series.id || 'series');
  const durationLabel = totalDurationSeconds
    ? formatTotalDuration(totalDurationSeconds)
    : series.release_schedule || 'Listen now';

  return {
    id: series.id,
    title: series.title,
    slug: seriesSlug,
    author: creatorName,
    creatorId: series.creator?.id ?? null,
    creatorSlug,
    creatorName,
    coverImage: series.cover_image_url ?? FALLBACK_COVER,
    duration: durationLabel,
    totalChapters: series.episodes?.length ?? 0,
    genreIds,
    description: series.description,
    releaseSchedule: series.release_schedule,
    listens: 0,
  };
};


export default function App() {
  const [stories, setStories] = useState<Story[]>([]);
  const [genres, setGenres] = useState<GenreRecord[]>([]);
  const [likedStories, setLikedStories] = useState<Set<string>>(new Set());
  const [continueListening, setContinueListening] = useState<ContinueListeningEntry[]>([]);
  const [newCreators, setNewCreators] = useState<CreatorSpotlight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [playerError, setPlayerError] = useState<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [profileAvatarUrl, setProfileAvatarUrl] = useState<string | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [pendingAuthRedirect, setPendingAuthRedirect] = useState<string | null>(null);

  const seriesSlugCacheRef = useRef<Map<string, string>>(new Map());
  const usedSeriesSlugsRef = useRef<Set<string>>(new Set());

  const location = useLocation();
  const navigate = useNavigate();

  const userId = session?.user?.id ?? DEMO_USER_ID ?? null;
  const showLibraryTab = Boolean(session);
  const isCreator = (profile?.role ?? '').toLowerCase() === 'creator';
  const isAdmin = (profile?.role ?? '').toLowerCase() === 'admin';
  const showCreatorTab = Boolean(session) && isCreator;
  const profileSlug = session?.user?.id ?? null;

  const homePath = routeBuilders.home();
  const explorePath = routeBuilders.explore();
  const loginPath = routeBuilders.login();
  const normalizedLoginPath = normalizePath(loginPath);
  const profilePath = profileSlug ? routeBuilders.profile(profileSlug) : null;
  const libraryPath = profileSlug ? routeBuilders.library(profileSlug) : null;
  const mySeriesPath = profileSlug ? routeBuilders.mySeries(profileSlug) : null;
  const creatorLandingName =
    profile?.display_name ?? session?.user?.email?.split('@')[0] ?? profileSlug ?? '';
  const creatorLandingPath =
    showCreatorTab && creatorLandingName ? routeBuilders.creatorPublic(slugify(creatorLandingName)) : null;
  const activePath = normalizePath(location.pathname);
  const playerController = usePlayerController();
  const playbackError = usePlayerStore((state) => state.playerError);
  const setPlaybackError = usePlayerStore((state) => state.setError);
  const currentSeriesId = usePlayerStore(
    (state) => state.queue.find((ep) => ep.id === state.currentEpisodeId)?.seriesId ?? null,
  );
  const isPlayerVisible = usePlayerStore(
    (state) => state.queue.length > 0 && Boolean(state.currentEpisodeId),
  );

  useEffect(() => {
    let isMounted = true;
    const resolveAvatarUrl = async () => {
      if (!profile?.avatar_url) {
        if (isMounted) setProfileAvatarUrl(null);
        return;
      }
      const trimmed = profile.avatar_url.trim();
      if (!trimmed) {
        if (isMounted) setProfileAvatarUrl(null);
        return;
      }
      if (trimmed.startsWith('http')) {
        if (isMounted) setProfileAvatarUrl(trimmed);
        return;
      }
      const storagePath = extractImageStoragePath(trimmed);
      if (!storagePath) {
        if (isMounted) setProfileAvatarUrl(trimmed);
        return;
      }
      const { data, error } = await supabase.storage
        .from(MEDIA_CONSTANTS.IMAGES_BUCKET)
        .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
      if (!isMounted) return;
      if (error || !data?.signedUrl) {
        setProfileAvatarUrl(trimmed);
        return;
      }
      setProfileAvatarUrl(data.signedUrl);
    };
    resolveAvatarUrl();
    return () => {
      isMounted = false;
    };
  }, [profile?.avatar_url]);

  const getDisplayImageUrl = useCallback(
    (raw?: string | null) => fetchDisplayImageUrl(raw),
    [],
  );

  const generateSeriesSlug = useCallback(
    (series: { id: string; slug?: string | null; title?: string | null }) => {
      const cache = seriesSlugCacheRef.current;
      const used = usedSeriesSlugsRef.current;
      const cached = cache.get(series.id);
      if (cached) return cached;

      const baseRaw = (series.slug || '').trim();
      const base = baseRaw || slugify(series.title || series.id || 'series');
      let candidate = base || 'series';

      while (used.has(candidate)) {
        const suffix = Math.floor(100 + Math.random() * 900);
        candidate = `${base}-${suffix}`;
      }

      used.add(candidate);
      cache.set(series.id, candidate);
      return candidate;
    },
    [],
  );

  const countFollowers = useCallback(async (creatorId: string): Promise<number> => {
    const { count, error } = await supabase
      .from('creator_follows')
      .select('follower_id', { count: 'exact', head: true })
      .eq('creator_id', creatorId);
    if (error) {
      const message = (error as any)?.message || '';
      const isMissingTable = message.includes('does not exist') || error.code === '42P01';
      if (!isMissingTable) {
        console.warn('Unable to count followers in creator_follows', error);
      }
      return 0;
    }
    return count ?? 0;
  }, []);

  const countPublishedSeries = useCallback(async (creatorId: string): Promise<number> => {
    const { count, error } = await supabase
      .from('series')
      .select('id', { count: 'exact', head: true })
      .eq('creator_id', creatorId)
      .eq('status', 'published');
    if (error) {
      console.error('Error counting published series for creator', error);
      return 0;
    }
    return count ?? 0;
  }, []);

  const loadContinueListening = useCallback(
    async (targetUserId: string): Promise<ContinueListeningEntry[]> => {
      const { data, error } = await supabase
        .from('listening_progress')
        .select(
          `
          episode_id,
          position_seconds,
          last_listened_at,
          is_completed,
          episodes:episode_id (
            id,
            title,
            slug,
            episode_number,
            duration_seconds,
            series:series_id (
              id,
              title,
              slug,
              description,
              release_schedule,
              cover_image_url,
              creator:profiles!series_creator_id_fkey (
                id,
                display_name
              )
            )
          )
        `,
        )
        .eq('user_id', targetUserId)
        .order('last_listened_at', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Error fetching listening progress for home', error);
        return [];
      }

      const activeEntries = (data ?? []).filter(
        (entry) => !entry.is_completed && entry.episodes?.series,
      );

      const mapped = await Promise.all(
        activeEntries.map(async (entry): Promise<ContinueListeningEntry> => {
          const episode = entry.episodes!;
          const series = episode.series!;
          const existingStory = stories.find((story) => story.id === series.id) ?? null;
          const coverImage =
            (await getDisplayImageUrl(series.cover_image_url ?? existingStory?.coverImage ?? null)) ??
            existingStory?.coverImage ??
            FALLBACK_COVER;
          const creatorName =
            series.creator?.display_name ??
            existingStory?.creatorName ??
            existingStory?.author ??
            'Unknown Creator';
          const creatorSlug = creatorName ? slugify(creatorName) : null;
          const seriesSlug = generateSeriesSlug(series);
          const story: Story =
            existingStory ??
            {
              id: series.id,
              title: series.title ?? 'Untitled Series',
              slug: seriesSlug,
              author: creatorName || 'Unknown Creator',
              creatorId: series.creator?.id ?? null,
              creatorSlug,
              creatorName,
              coverImage,
              duration: formatEpisodeDuration(episode.duration_seconds),
              totalChapters: 0,
              genreIds: [],
              description: series.description ?? null,
              releaseSchedule: series.release_schedule ?? null,
              listens: 0,
            };

          return {
            seriesId: series.id,
            seriesSlug: series.slug ?? story.slug,
            story,
            coverImage,
            creatorName,
            creatorSlug,
            episodeId: episode.id,
            episodeTitle: episode.title,
            episodeSlug: episode.slug,
            episodeNumber: episode.episode_number,
            positionSeconds: entry.position_seconds ?? 0,
            durationSeconds: episode.duration_seconds,
            lastListenedAt: entry.last_listened_at,
          };
        }),
      );

      return mapped;
    },
    [generateSeriesSlug, getDisplayImageUrl, stories],
  );

  const loadNewCreators = useCallback(async (): Promise<CreatorSpotlight[]> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, display_name, avatar_url, created_at')
      .eq('role', 'creator')
      .order('created_at', { ascending: false })
      .limit(4);

    if (error) {
      console.error('Error fetching new creators for home', error);
      return [];
    }

    const creators = await Promise.all(
      (data ?? []).map(async (creator) => {
        const [avatarUrl, followerCount, seriesCount] = await Promise.all([
          getDisplayImageUrl(creator.avatar_url ?? null),
          countFollowers(creator.id),
          countPublishedSeries(creator.id),
        ]);
        return {
          id: creator.id,
          displayName: creator.display_name ?? 'New Creator',
          avatarUrl: avatarUrl ?? FALLBACK_COVER,
          followerCount,
          seriesCount,
          slug: slugify(creator.display_name || creator.id),
        };
      }),
    );

    return creators;
  }, [countFollowers, countPublishedSeries, getDisplayImageUrl]);

  // Fetch initial data
  useEffect(() => {
    const initData = async () => {
      try {
        setIsLoading(true);
        setPlayerError(null);

        const [seriesResponse, genresResponse] = await Promise.all([
          supabase
            .from('series')
            .select(
              `
              id,
              title,
              slug,
              description,
              cover_image_url,
              release_schedule,
              status,
              creator:profiles!series_creator_id_fkey (
                id,
                display_name
              ),
              series_genres (
                genre_id,
                genres (
                  id,
                  name,
                  slug
                )
              ),
              episodes (
                duration_seconds,
                slug
              )
            `,
            )
            .eq('status', 'published')
            .order('published_at', { ascending: false }),
          supabase.from('genres').select('id, name, slug').order('name', { ascending: true }),
        ]);

        if (seriesResponse.error) throw seriesResponse.error;
        if (genresResponse.error) throw genresResponse.error;

        const seriesWithImages = await Promise.all(
          (seriesResponse.data ?? []).map(async (series) => ({
            ...series,
            cover_image_url: await getDisplayImageUrl(series.cover_image_url),
          })),
        );

        const normalizedStories = seriesWithImages.map((series) =>
          normalizeSeriesToStory(series, generateSeriesSlug(series)),
        );
        setStories(normalizedStories);
        setGenres(genresResponse.data ?? []);
      } catch (error) {
        console.error('Error fetching data from Supabase:', error);
        setPlayerError('Unable to load data from Supabase. Please try again shortly.');
      } finally {
        setIsLoading(false);
      }
    };

    initData();
  }, [generateSeriesSlug, getDisplayImageUrl]);

  useEffect(() => {
    let isMounted = true;
    if (!userId) {
      setContinueListening([]);
      return;
    }
    (async () => {
      const entries = await loadContinueListening(userId);
      if (isMounted) {
        setContinueListening(entries);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [loadContinueListening, userId]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      const creators = await loadNewCreators();
      if (isMounted) {
        setNewCreators(creators);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [loadNewCreators]);

  // Listen for auth state changes
  useEffect(() => {
    let isMounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (isMounted) {
        setSession(data.session);
      }
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Load profile for current user
  const refreshProfile = useCallback(
    async (profileUserId: string) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('display_name, avatar_url, bio, role')
        .eq('id', profileUserId)
        .maybeSingle();
      if (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } else {
        setProfile(data ?? null);
      }
    },
    [],
  );

  useEffect(() => {
    if (!session?.user?.id) {
      setProfile(null);
      return;
    }
    refreshProfile(session.user.id);
  }, [session, refreshProfile]);

  useEffect(() => {
    if (!session?.user?.id) return;
    if (!profilePath && !mySeriesPath) return;
    const isProfileArea =
      (profilePath && activePath.startsWith(normalizePath(profilePath))) ||
      (mySeriesPath && activePath.startsWith(normalizePath(mySeriesPath)));
    if (isProfileArea) {
      refreshProfile(session.user.id);
    }
  }, [activePath, mySeriesPath, profilePath, refreshProfile, session?.user?.id]);

  // Fetch likes whenever user changes
  useEffect(() => {
    let isMounted = true;
    if (!userId) {
      setLikedStories(new Set());
      return;
    }

    const fetchLikes = async () => {
      const { data, error } = await supabase.from('likes').select('series_id').eq('user_id', userId);
      if (!isMounted) return;
      if (error) {
        console.error('Error fetching likes:', error);
        return;
      }
      setLikedStories(
        new Set(
          (data ?? [])
            .map((like) => like.series_id)
            .filter((seriesId): seriesId is string => Boolean(seriesId)),
        ),
      );
    };

    fetchLikes();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (activePath === normalizedLoginPath && !isAuthModalOpen) {
      setIsAuthModalOpen(true);
    }
  }, [activePath, isAuthModalOpen, normalizedLoginPath]);

  useEffect(() => {
    if (!session) return;
    const target =
      pendingAuthRedirect || (activePath === normalizedLoginPath ? profilePath || homePath : null);
    if (!target) return;
    navigate(normalizePath(target), { replace: true });
    setPendingAuthRedirect(null);
    setIsAuthModalOpen(false);
  }, [activePath, homePath, navigate, normalizedLoginPath, pendingAuthRedirect, profilePath, session]);

  const handleNavigate = (path: string, requiresAuth = false) => {
    const normalized = normalizePath(path);
    if (requiresAuth && !session) {
      setPendingAuthRedirect(normalized);
      setIsAuthModalOpen(true);
      navigate(normalizedLoginPath, { state: { from: normalized } });
      return;
    }
    navigate(normalized);
  };

  const handlePlayStory = async (story: Story, options?: { startEpisodeId?: string }) => {
    try {
      setPlayerError(null);
      await playerController.startSeriesPlayback(
        {
          id: story.id,
          title: story.title,
          slug: story.slug,
          author: story.author,
          creatorSlug: story.creatorSlug ?? (story.author ? slugify(story.author) : null),
          coverImage: story.coverImage,
          creatorName: story.creatorName ?? story.author,
        },
        options,
      );
    } catch (error) {
      console.error('Error starting playback:', error);
      setPlayerError('Unable to start playback. Please try again.');
    }
  };

  const handleMarkComplete = () => {
    playerController.markCompleted();
  };

  const handleLikeStory = async (storyId: string) => {
    if (!userId) {
      setPlayerError('Please log in to like stories.');
      return;
    }

    try {
      const isLiked = likedStories.has(storyId);

      if (isLiked) {
        const { error } = await supabase
          .from('likes')
          .delete()
          .eq('user_id', userId)
          .eq('series_id', storyId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('likes').insert({ user_id: userId, series_id: storyId });
        if (error) throw error;
      }

      setLikedStories((prev) => {
        const newSet = new Set(prev);
        if (isLiked) {
          newSet.delete(storyId);
        } else {
          newSet.add(storyId);
        }
        return newSet;
      });
    } catch (error) {
      console.error('Error toggling like:', error);
      setPlayerError('Unable to update likes right now. Please try again.');
    }
  };

  const handleOpenAuth = (redirectTo?: string | MouseEvent) => {
    const requestedTarget = typeof redirectTo === 'string' ? redirectTo : undefined;
    const desiredTarget = requestedTarget
      ? normalizePath(requestedTarget)
      : activePath === normalizedLoginPath
        ? homePath
        : activePath;
    if (!pendingAuthRedirect && desiredTarget) {
      setPendingAuthRedirect(normalizePath(desiredTarget));
    }
    setIsAuthModalOpen(true);
    if (!session && activePath !== normalizedLoginPath) {
      navigate(normalizedLoginPath);
    }
  };

  const handleCloseAuth = () => {
    setIsAuthModalOpen(false);
    if (!session && activePath === normalizedLoginPath) {
      navigate(homePath);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setPendingAuthRedirect(null);
    navigate(homePath);
  };

  const handleAuthSuccess = () => {
    setIsAuthModalOpen(false);
    if (!pendingAuthRedirect) {
      setPendingAuthRedirect(profilePath || homePath);
    }
  };

  const RequireAuth = () => {
    const routeLocation = useLocation();
    useEffect(() => {
      if (session) return;
      const attempted = normalizePath(routeLocation.pathname);
      if (!pendingAuthRedirect) {
        setPendingAuthRedirect(attempted);
      }
      setIsAuthModalOpen((open) => (open ? open : true));
    }, [pendingAuthRedirect, routeLocation.pathname, session]);

    if (!session) {
      return <Navigate to={loginPath} state={{ from: routeLocation.pathname }} replace />;
    }
    return <Outlet />;
  };

  const ProfileSlugGuard = () => {
    const { profileSlug: routeSlug } = useParams();
    const routeLocation = useLocation();
    if (!profileSlug) {
      return <Navigate to={loginPath} replace />;
    }
    if (routeSlug && routeSlug !== profileSlug) {
      const suffix = routeLocation.pathname.slice(
        routeLocation.pathname.indexOf(routeSlug) + routeSlug.length,
      );
      const target = `${routeBuilders.profile(profileSlug)}${suffix}`;
      return <Navigate to={normalizePath(target)} replace />;
    }
    return <Outlet />;
  };

  const MySeriesRoute = () => {
    const { seriesSlug } = useParams();
    if (!userId || !profileSlug) return null;
    if (!showCreatorTab) {
      return (
        <div className="page-shell text-center text-base-content/60">
          <p>This section is available for creator accounts only.</p>
        </div>
      );
    }
    return (
      <MySeries
        userId={userId}
        genres={genres}
        initialSeriesSlug={seriesSlug ?? null}
        onRouteChange={(slug, options) => {
          const target = slug
            ? routeBuilders.seriesEdit(profileSlug, slug)
            : routeBuilders.mySeries(profileSlug);
          const state = options?.startNewSeries ? { startNewSeries: true } : undefined;
          navigate(normalizePath(target), { replace: true, state });
        }}
      />
    );
  };

  const LoginPage = () => (
    <div className="page-shell text-center text-base-content/70 space-y-4">
      <h1 className="text-3xl font-bold">Login required</h1>
      <p className="max-w-xl mx-auto">Sign in to access your profile, library, and creator tools.</p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => handleOpenAuth()}
          className="btn btn-primary"
        >
          Open login
        </button>
        <button
          onClick={() => navigate(homePath)}
          className="btn btn-outline btn-primary"
        >
          Back home
        </button>
      </div>
    </div>
  );

  const AdminPanelRoute = () => {
    const routeLocation = useLocation();
    if (routeLocation.pathname !== '/admin-panel/') {
      return <Navigate to={homePath} replace />;
    }
    return <AdminPanel isAdmin={isAdmin} onNavigateHome={() => navigate(homePath)} />;
  };

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-base-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-base-300 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-base-content/60">Loading your stories...</p>
        </div>
      </div>
    );
  }

  const contentPadding = isPlayerVisible ? 'pt-0 pb-28 md:pb-32' : 'pt-0';
  const combinedPlayerError = playerError || playbackError;

  return (
    <div className="w-full min-h-screen bg-base-100">
      <Navigation
        variant="header"
        currentPath={activePath}
        onNavigate={handleNavigate}
        isAuthenticated={Boolean(session)}
        onLoginClick={handleOpenAuth}
        onSignOut={handleSignOut}
        profilePath={profilePath ?? undefined}
        profileImageUrl={profileAvatarUrl ?? profile?.avatar_url ?? null}
        showLibrary={showLibraryTab && Boolean(libraryPath)}
        libraryPath={libraryPath ?? undefined}
        showCreator={showCreatorTab && Boolean(mySeriesPath)}
        creatorPath={mySeriesPath ?? undefined}
        creatorLandingPath={creatorLandingPath ?? undefined}
      />

      {/* Main Content */}
      <div className={`block ${contentPadding}`}>
        <Routes>
          <Route
            path="/series/:seriesSlug"
            element={
              <SeriesLanding
                session={session}
                onLikeStory={handleLikeStory}
                likedStories={likedStories}
                onPlayStory={handlePlayStory}
              />
            }
          />
          <Route
            path={homePath}
            element={
              <Home
                stories={stories}
                continueListening={continueListening}
                newCreators={newCreators}
                onPlayStory={handlePlayStory}
                onLikeStory={handleLikeStory}
                likedStories={likedStories}
              />
            }
          />
          <Route
            path={explorePath}
            element={
              <Explore
                stories={stories}
                genres={genres}
                onPlayStory={handlePlayStory}
                onLikeStory={handleLikeStory}
                likedStories={likedStories}
              />
            }
          />
          <Route path="/creator/:creatorSlug" element={<CreatorLanding session={session} />} />
          <Route element={<RequireAuth />}>
            <Route element={<ProfileSlugGuard />}>
              <Route
                path="/profile/:profileSlug"
                element={
                  <UserProfile
                    profile={profile}
                    session={session as Session}
                    onProfileUpdated={() => {
                      if (session?.user?.id) {
                        refreshProfile(session.user.id);
                      }
                    }}
                    onSignOut={handleSignOut}
                  />
                }
              />
              <Route
                path="/profile/:profileSlug/library"
                element={<Library stories={stories} likedStories={likedStories} onPlayStory={handlePlayStory} />}
              />
              <Route path="/profile/:profileSlug/series/:seriesSlug?" element={<MySeriesRoute />} />
            </Route>
          </Route>
          <Route path={loginPath} element={<LoginPage />} />
          <Route path="/admin-panel/" element={<AdminPanelRoute />} />
          <Route path="*" element={<Navigate to={homePath} replace />} />
        </Routes>
        <Navigation
          currentPath={activePath}
          onNavigate={handleNavigate}
          isAuthenticated={Boolean(session)}
          onLoginClick={handleOpenAuth}
          onSignOut={handleSignOut}
          profilePath={profilePath ?? undefined}
          profileImageUrl={profileAvatarUrl ?? profile?.avatar_url ?? null}
          showLibrary={showLibraryTab && Boolean(libraryPath)}
          libraryPath={libraryPath ?? undefined}
          showCreator={showCreatorTab && Boolean(mySeriesPath)}
          creatorPath={mySeriesPath ?? undefined}
          creatorLandingPath={creatorLandingPath ?? undefined}
        />
      </div>

      <DockPlayer
        onToggleLike={handleLikeStory}
        isLiked={currentSeriesId ? likedStories.has(currentSeriesId) : false}
        onMarkComplete={handleMarkComplete}
      />
      <QueueDrawer />
      <FullPlayer
        onToggleLike={handleLikeStory}
        isLiked={currentSeriesId ? likedStories.has(currentSeriesId) : false}
        onMarkComplete={handleMarkComplete}
      />

      {combinedPlayerError && (
        <div className="fixed bottom-24 left-0 right-0 px-6 z-40 pointer-events-none">
          <div className="alert alert-error max-w-lg mx-auto shadow-lg pointer-events-auto">
            <p className="flex-1 text-sm">{combinedPlayerError}</p>
            <button
              onClick={() => {
                if (playerError) setPlayerError(null);
                if (playbackError) setPlaybackError(null);
              }}
              className="btn btn-ghost btn-error btn-xs"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <AuthModal isOpen={isAuthModalOpen} onClose={handleCloseAuth} onAuthSuccess={handleAuthSuccess} />
    </div>
  );
}
